import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, Terminal, ArrowLeftRight, AlertTriangle, Table2, BookOpenCheck } from 'lucide-react';
import Breadcrumbs from '../../components/ui/Breadcrumbs.tsx';
import styles from '../../styles/Ariba.module.css';

interface AribaProps {
  requireAuth: (callback: () => void) => void;
  setPage: (page: string) => void;
}

interface ModuleCardProps {
  title: string;
  desc: string;
  // Emoji (string) para la mayoría de tarjetas, o un ícono de lucide-react
  // cuando se pide uno "técnico" en vez de emoji (ver BAdIs y Objetos ABAP).
  icon: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary';
}

const ModuleCard: React.FC<ModuleCardProps> = ({ title, desc, icon, onClick, variant = 'primary' }) => (
  <div 
    onClick={onClick} 
    className={`${styles.glassCard} p-8 rounded-[2rem] cursor-pointer group transition-all duration-300 ${
      variant === 'secondary' ? 'bg-white/5 border-dashed border-white/20' : ''
    }`}
  >
    <div className="text-5xl mb-6 transition-transform duration-500 group-hover:scale-125 drop-shadow-lg">
      {icon}
    </div>
    <h2 className="text-xl font-semibold mb-3 tracking-wide group-hover:text-blue-300 transition-colors">
      {title}
    </h2>
    <p className="text-sm text-gray-400 font-light leading-relaxed">
      {desc}
    </p>
  </div>
);

export default function Ariba({ requireAuth, setPage }: AribaProps) {
  const navigate = useNavigate();

  return (
    <main className={styles.mainContainer}>
      <div className={`${styles.blob} top-[-10%] left-[-5%] w-[500px] h-[500px] bg-blue-600`} />
      <div className={`${styles.blob} bottom-0 right-0 w-[400px] h-[400px] bg-purple-600`} />

      <div className="relative z-20 mt-4">
        <Breadcrumbs currentPage="ariba" setPage={setPage} />
      </div>

      <header className="relative z-10 flex flex-col items-center mb-16 mt-10">
        <div className="text-7xl mb-6 drop-shadow-[0_0_20px_rgba(59,130,246,0.4)]">
          📦
        </div>
        <h1 className={`${styles.titleGradient} text-5xl md:text-6xl font-bold mb-4 tracking-tight`}>
          SAP Ariba
        </h1>
        <p className="text-gray-400 text-center max-w-2xl text-lg font-light italic">
          "Gestión modular y herramientas transversales para el ecosistema Ariba"
        </p>
      </header>

      {/* --- SECCIÓN 1: MÓDULOS CORE --- */}
      <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 w-full max-w-6xl mb-20">
        <ModuleCard
          title="Commerce Automation"
          desc="Procesamiento de facturas, órdenes y documentos electrónicos."
          icon="📄"
          onClick={() => requireAuth(() => navigate('/proyectos'))}
        />
        <ModuleCard
          title="SLP"
          desc="Gestión del ciclo de vida de proveedores y cumplimiento."
          icon="🧑‍💼"
          onClick={() => requireAuth(() => navigate('/proyectos'))}
        />
        <ModuleCard
          title="Sourcing"
          desc="Eventos de sourcing, RFQ, RFP y subastas en tiempo real."
          icon="📊"
          onClick={() => requireAuth(() => navigate('/proyectos'))}
        />
        <ModuleCard
          title="Contracts"
          desc="Gestión centralizada de contratos y repositorio legal."
          icon="📑"
          onClick={() => requireAuth(() => navigate('/proyectos'))}
        />
        <ModuleCard
          title="Buying"
          desc="Experiencia de compra guiada y catálogos inteligentes."
          icon="🛒"
          onClick={() => requireAuth(() => navigate('/proyectos'))}
        />
        <ModuleCard
          title="CIG"
          desc="Transformación, Mapeos e integracion entre Ariba / ERP - No ERP."
          icon="🔄"
          onClick={() => requireAuth(() => navigate('/proyectos'))}
        />
      </div>

      {/* --- SECCIÓN 2: HERRAMIENTAS GENERALES (TOOLBOX) --- */}
      <div className="relative z-10 w-full max-w-6xl pb-20">
        <div className="flex items-center gap-4 mb-8">
          <h2 className="text-2xl font-semibold text-white/80">Ariba Toolbox</h2>
          <div className="h-[1px] flex-grow bg-gradient-to-r from-white/20 to-transparent"></div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <ModuleCard
            title="Guía Habilitación APIs"
            desc="Explorador de APIs, pruebas en vivo y documentación técnica."
            icon="🔗"
            variant="secondary"
            onClick={() => requireAuth(() => setPage('ariba-apis'))} 
          />
          <ModuleCard
            title="Envio documentos, Ariba <-> ERP"
            desc="Reenvío de mensajes y auditoría de payloads mediante Body/CIG."
            icon="🔄"
            variant="secondary"
            onClick={() => requireAuth(() => setPage('cig-send'))}
          />
          <ModuleCard
            title="Validador de Estructura"
            desc="Compara un XML contra una plantilla de referencia que ya funcionó: longitud de campos, tags faltantes o inesperados."
            icon="🧬"
            variant="secondary"
            onClick={() => requireAuth(() => setPage('validador-estructura'))}
          />
          <ModuleCard
            title="BAdIs y Objetos ABAP"
            desc="Repositorio de BAdIs estándar, clases, métodos, programas e interfaces ABAP para integraciones SAP."
            icon={<Database className="w-10 h-10 text-blue-400" />}
            variant="secondary"
            onClick={() => requireAuth(() => setPage('badis-toolbox'))}
          />
          <ModuleCard
            title="Transacciones SAP"
            desc="Guía y diccionario rápido de transacciones de desarrollo, monitoreo y compras en SAP."
            icon={<Terminal className="w-10 h-10 text-blue-400" />}
            variant="secondary"
            onClick={() => requireAuth(() => setPage('transacciones-toolbox'))}
          />
          <ModuleCard
            title="Interfaces / Canales"
            desc="Catálogo de interfaces de integración Ariba ↔ ERP ↔ S/4HANA ↔ BTP: por dónde entra/sale cada flujo y en qué transacción se monitorea."
            icon={<ArrowLeftRight className="w-10 h-10 text-blue-400" />}
            variant="secondary"
            onClick={() => requireAuth(() => setPage('interfaces-toolbox'))}
          />
          <ModuleCard
            title="Tablas SAP"
            desc="Catálogo de tablas estándar (ej. vía SE16N) con sus campos, para las integraciones SAP Ariba / S/4HANA."
            icon={<Table2 className="w-10 h-10 text-blue-400" />}
            variant="secondary"
            onClick={() => requireAuth(() => setPage('tablas-toolbox'))}
          />
          <ModuleCard
            title="Errores Conocidos"
            desc="Errores documentados de integración SAP Ariba ↔ ERP con causa, solución y referencia a notas oficiales."
            icon={<AlertTriangle className="w-10 h-10 text-blue-400" />}
            variant="secondary"
            onClick={() => requireAuth(() => setPage('errores-conocidos-toolbox'))}
          />
          <ModuleCard
            title="Guías de Integración"
            desc="Checklist de configuración estándar por combinación Solución + ERP, para arrancar un proyecto sabiendo qué falta configurar."
            icon={<BookOpenCheck className="w-10 h-10 text-blue-400" />}
            variant="secondary"
            onClick={() => requireAuth(() => setPage('guias-integracion-toolbox'))}
          />
          {/* <ModuleCard
            title="Status Manager"
            desc="Envío y actualización masiva de estados para documentos."
            icon="⚡"
            variant="secondary"
            onClick={() => requireAuth(() => setPage('status-manager'))} 
          /> */}
        </div>
      </div>
    </main>
  );
}